import type { MouseEvent } from "react";
import { LEGAL_COMPLIANCE_ALLOWED_TAGS, LEGAL_COMPLIANCE_CONTENT_CLASS } from "@/features/legal/config/legal.config";
import type { LegalDocument } from "@/features/legal/types/legalCompliance.type";
import { SerializeHTML } from "@/shared/components/SerializeHTML";

type LegalComplianceBodyProps = {
  bodyHtml: string
  documents: LegalDocument[]
  onOpenDocument: (document: LegalDocument) => void
};

// Links in the text must never navigate away from the login step: a link to
// a notice frozen with this version opens it in place, any other link opens
// in a new tab.
export const LegalComplianceBody = ({ bodyHtml, documents, onOpenDocument }: LegalComplianceBodyProps) => {
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest("a");
    const href = link?.getAttribute("href");
    if (!href) return;

    event.preventDefault();
    const document = documents.find((candidate) => candidate.url === href);
    if (document) {
      onOpenDocument(document);
      return;
    }
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    // Delegates clicks on the links, which stay natively focusable and keyboard-activable.
    <div className="text-base leading-7 text-gray-700 dark:text-gray-300" onClick={handleClick} data-testid="legal-compliance-body">
      <SerializeHTML
        htmlContent={bodyHtml}
        allowedTags={LEGAL_COMPLIANCE_ALLOWED_TAGS}
        className={LEGAL_COMPLIANCE_CONTENT_CLASS}
      />
    </div>
  );
};
